import { CircularProgress } from "@mui/material";
import { Feature, FeatureCollection } from "geojson";
import {
  ArableField,
  getAllArableFieldGeomsFromFields,
  getArableFieldsCenterWithNoTilesForNotDeletedBoundariesFromArray,
  getArableFieldsWithNoTilesForNotDeletedBoundariesFromArray,
  mapArableFieldsCentersByIdFromArray,
  useArableFields,
} from "model";
import { MutableRefObject, useMemo } from "react";
import { Layer, MapRef, Marker, Source } from "react-map-gl/mapbox";
import { useMatch } from "react-router-dom";
import { useUIStore } from "stores";
import DiaryEntryLocationMarkerWithScroll from "./DiaryEntryLocationMarkerWithScroll";

interface Props {
  editedFieldId?: string;
  fieldId?: string;
  fieldIds?: string[];
  fillLayers?: boolean;
  mapRef: MutableRefObject<MapRef | null>;
  setTooltipOverMarker: (value: boolean) => void;
  /** Current map zoom level - used to show fills when zoomed out (below tile threshold) */
  viewportZoom?: number;
  /** Whether tiles are ready to display - if false, show solid fills even when zoomed in */
  tilesReady?: boolean;
}

interface MatchProps {
  id: string;
}

export default function ArableFieldGeoJSONLayers({
  editedFieldId,
  fieldId,
  fieldIds,
  fillLayers,
  mapRef,
  setTooltipOverMarker,
  viewportZoom,
  tilesReady,
}: Props) {
  const editMatch = useMatch("/edit-field/:id/*");

  // Use React Query for arable fields data
  const { data: allFields = [] } = useArableFields();

  // Use Zustand for UI state
  const tmpDeletedFeatures = useUIStore((state) => state.tmpDeletedFeatures);

  // Compute field geoms from React Query data
  const fieldGeoms = useMemo(
    () => getAllArableFieldGeomsFromFields(allFields),
    [allFields]
  );

  // Compute arable field centers
  const arableFieldCentersById = useMemo(
    () => mapArableFieldsCentersByIdFromArray(allFields),
    [allFields]
  );

  // Get current arable field geom
  const currentArableFieldGeom = useMemo((): Feature | undefined => {
    if (!fieldId) return undefined;
    const field = allFields.find((f: ArableField) => f.guid === fieldId);
    return field?.geom as Feature | undefined;
  }, [allFields, fieldId]);

  // Compute arable fields with no tiles from React Query data
  const arableFieldsWithNoTiles = useMemo(() => {
    const noTilesFields =
      getArableFieldsWithNoTilesForNotDeletedBoundariesFromArray(
        allFields,
        tmpDeletedFeatures
      );
    return noTilesFields.filter((fieldGuid) => {
      // Always exclude edited field
      if (fieldGuid === editedFieldId) return false;

      // If fieldIds is provided, only include fields from that list
      if (fieldIds && fieldIds.length > 0) {
        return fieldIds.includes(fieldGuid);
      }

      // Otherwise include all fields
      return true;
    });
  }, [allFields, tmpDeletedFeatures, editedFieldId, fieldIds]);

  // Compute arable field centers with no tiles from React Query data
  const arableFieldsWithNoTilesCenters = useMemo(() => {
    const centers =
      getArableFieldsCenterWithNoTilesForNotDeletedBoundariesFromArray(
        allFields,
        tmpDeletedFeatures,
        arableFieldCentersById
      );
    return centers.filter(
      (field) => field.guid !== editedFieldId && fieldIds?.includes(field.guid)
    );
  }, [
    allFields,
    tmpDeletedFeatures,
    arableFieldCentersById,
    editedFieldId,
    fieldIds,
  ]);

  const filteredFieldGeoms = useMemo(() => {
    // If no fieldIds provided, return all fields
    if (!fieldIds || fieldIds.length === 0) return fieldGeoms;

    // Otherwise filter to only show specified fields
    return {
      type: "FeatureCollection",
      features: fieldGeoms.features.filter((feature) =>
        fieldIds.includes(feature.properties?.id as string)
      ),
    };
  }, [fieldGeoms, fieldIds]);

  const zoom = mapRef.current?.getMap()?.getZoom();

  // Show solid fill colors when:
  // 1. On mobile (fillLayers prop is true), OR
  // 2. When tiles are not ready yet (smooth transition - avoid empty fields flash)
  const showSolidFills = fillLayers || !tilesReady;

  return (
    <>
      <Source
        id="arable-field-outlines"
        data={filteredFieldGeoms as FeatureCollection}
        type="geojson"
      >
        <Layer
          id="arable-field-fill"
          type="fill"
          //filter out fill of field thats being edited
          filter={["!=", "id", editMatch?.params.id || ""]}
          paint={{
            "fill-opacity": showSolidFills ? 1.0 : 0,
            "fill-color": showSolidFills ? "#FADF75" : "transparent",
          }}
        />
        <Layer
          id="arable-field-fill-no-data"
          type="fill"
          //only show this if a field has no tiles
          filter={["in", ["get", "id"], ["literal", arableFieldsWithNoTiles]]}
          paint={{ "fill-color": "#666666" }}
        />
        <Layer
          id="arable-outline"
          type="line"
          paint={{
            "line-color": "#666666",
            "line-width": [
              "interpolate",
              ["exponential", 2],
              ["zoom"],
              10,
              ["*", 5, ["^", 2, -6]],
              24,
              ["*", 5, ["^", 2, 7]],
            ],
            "line-offset": [
              "interpolate",
              ["exponential", 2],
              ["zoom"],
              10,
              ["*", 5 / 2 - 1, ["^", 2, -6]],
              24,
              ["*", 5 / 2 - 1, ["^", 2, 5]],
            ],
          }}
          layout={{
            "line-join": "miter",
          }}
        />
      </Source>
      {currentArableFieldGeom &&
        fieldId &&
        fieldId !== editedFieldId &&
        tmpDeletedFeatures.indexOf(fieldId) === -1 && (
          <Source
            id="arable-field-highlight"
            data={currentArableFieldGeom}
            type="geojson"
          >
            <Layer
              id="arable-field-highlight"
              type="line"
              paint={{
                "line-color": "#FFFFFF",
                "line-width": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5, ["^", 2, -6]],
                  24,
                  ["*", 5, ["^", 2, 7]],
                ],
                "line-offset": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5 / 2 - 1, ["^", 2, -6]],
                  24,
                  ["*", 5 / 2 - 1, ["^", 2, 5]],
                ],
              }}
              layout={{
                "line-join": "miter",
              }}
            />
          </Source>
        )}
      {zoom &&
        zoom >= 13 &&
        !editedFieldId &&
        allFields.map((field: ArableField) => {
          return (
            <DiaryEntryLocationMarkerWithScroll
              setTooltipOverMarker={setTooltipOverMarker}
              key={`arable-diary-locarion-markers-${field.guid}`}
              zoom={zoom}
              currentField={field}
            />
          );
        })}

      {zoom &&
        zoom >= 15 &&
        arableFieldsWithNoTilesCenters.map((field) => {
          const { coordinates } = field.center.geometry;
          return (
            <Marker
              key={`arable-no-tiles-field-${field.guid}`}
              longitude={coordinates[0]}
              latitude={coordinates[1]}
            >
              <CircularProgress size={20} />
            </Marker>
          );
        })}
    </>
  );
}
